import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { 
  Package, Search, Plus, Edit2, Trash2, Upload, Download, 
  Check, X, AlertTriangle, Filter, MoreVertical, Image,
  DollarSign, Percent, Archive, Star, Tag, ChevronLeft, ChevronRight,
  Loader2
} from 'lucide-react';

const CATEGORIES = ['Electronics', 'Clothing', 'Books', 'Wellness', 'Accessories', 'Food', 'Home', 'Other'];

export default function ManageProducts() {
  const { token } = useAuth();
  const { currentColors } = useTheme();
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('');
  const [pagination, setPagination] = useState({ skip: 0, limit: 20, total: 0 });
  
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [importing, setImporting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    description: '',
    category: 'Other',
    price: '',
    cost_price: '',
    profit_margin: '',
    discount_percent: '0',
    stock_quantity: '0',
    low_stock_threshold: '10',
    weight: '',
    image_url: '',
    is_active: true,
    is_featured: false,
    tags: ''
  });

  useEffect(() => {
    loadProducts();
  }, [search, categoryFilter, stockFilter, pagination.skip]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: pagination.skip,
        limit: pagination.limit
      });
      if (search) params.append('search', search);
      if (categoryFilter) params.append('category', categoryFilter);
      if (stockFilter === 'low') params.append('low_stock', 'true');
      if (stockFilter === 'out') params.append('is_active', 'true');
      
      const response = await axios.get(`${API}/admin/products?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data.products);
      setPagination(prev => ({ ...prev, total: response.data.total }));
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        cost_price: parseFloat(formData.cost_price) || 0,
        profit_margin: formData.profit_margin ? parseFloat(formData.profit_margin) : null,
        discount_percent: parseFloat(formData.discount_percent) || 0,
        stock_quantity: parseInt(formData.stock_quantity) || 0,
        low_stock_threshold: parseInt(formData.low_stock_threshold) || 10,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t)
      };

      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/admin/products`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      loadProducts();
      resetForm();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadProducts();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete product');
    }
  };

  const handleBulkAction = async (action, value = null) => {
    if (selectedProducts.length === 0) return;
    try {
      await axios.post(`${API}/admin/products/bulk`, {
        product_ids: selectedProducts,
        action,
        value
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadProducts();
      setSelectedProducts([]);
      setShowBulkActions(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Bulk action failed');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImage(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload/product-image`, uploadData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      // Set the uploaded image URL to form
      setFormData(prev => ({ ...prev, image_url: response.data.url }));
    } catch (error) {
      alert(error.response?.data?.detail || 'Image upload failed');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/admin/products/import-csv`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      alert(`${response.data.message}\n${response.data.errors.length > 0 ? 'Errors: ' + response.data.errors.join('\n') : ''}`);
      loadProducts();
    } catch (error) {
      alert(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/products/export-csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'products_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Export failed');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingProduct(null);
    setFormData({
      sku: '',
      name: '',
      description: '',
      category: 'Other',
      price: '',
      cost_price: '',
      profit_margin: '',
      discount_percent: '0',
      stock_quantity: '0',
      low_stock_threshold: '10',
      weight: '',
      image_url: '',
      is_active: true,
      is_featured: false,
      tags: ''
    });
  };

  const editProduct = (product) => {
    setEditingProduct(product);
    setFormData({
      sku: product.sku,
      name: product.name,
      description: product.description || '',
      category: product.category,
      price: product.price.toString(),
      cost_price: product.cost_price.toString(),
      profit_margin: product.profit_margin?.toString() || '',
      discount_percent: product.discount_percent.toString(),
      stock_quantity: product.stock_quantity.toString(),
      low_stock_threshold: product.low_stock_threshold.toString(),
      weight: product.weight?.toString() || '',
      image_url: product.image_url || '',
      is_active: product.is_active,
      is_featured: product.is_featured,
      tags: product.tags?.join(', ') || ''
    });
    setShowForm(true);
  };

  const toggleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.id));
    }
  };

  const toggleSelect = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  // Calculate profit preview
  const calculateProfitPreview = () => {
    const price = parseFloat(formData.price) || 0;
    const cost = parseFloat(formData.cost_price) || 0;
    const discount = parseFloat(formData.discount_percent) || 0;
    const salePrice = price * (1 - discount / 100);
    const profit = salePrice - cost;
    const margin = salePrice > 0 ? (profit / salePrice * 100) : 0;
    return { salePrice: salePrice.toFixed(2), profit: profit.toFixed(2), margin: margin.toFixed(1) };
  };

  const profitPreview = calculateProfitPreview();

  return (
    <div className="space-y-6" data-testid="manage-products-page">
      {/* Header */}
      <div className="glass-card p-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Package className="w-8 h-8" style={{ color: currentColors.primary }} />
            <div>
              <h1 className="text-2xl font-cinzel" style={{ color: currentColors.primary }}>
                Product Management
              </h1>
              <p className="text-sm" style={{ color: currentColors.textMuted }}>
                {pagination.total} products total
              </p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImportCSV}
              accept=".csv"
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all"
              style={{ backgroundColor: `${currentColors.secondary}20`, color: currentColors.secondary }}
            >
              <Upload className="w-4 h-4" />
              {importing ? 'Importing...' : 'Import CSV'}
            </button>
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all"
              style={{ backgroundColor: `${currentColors.secondary}20`, color: currentColors.secondary }}
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="cosmic-btn flex items-center gap-2 text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Product
            </button>
          </div>
        </div>
        
        {/* Filters */}
        <div className="flex flex-wrap gap-4 mt-6">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: currentColors.textMuted }} />
            <input
              type="text"
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="cosmic-input w-full pl-10"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="cosmic-input"
          >
            <option value="">All Categories</option>
            {CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <select
            value={stockFilter}
            onChange={(e) => setStockFilter(e.target.value)}
            className="cosmic-input"
          >
            <option value="">All Stock</option>
            <option value="low">Low Stock</option>
            <option value="out">Out of Stock</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedProducts.length > 0 && (
        <div className="glass-card p-4 flex items-center justify-between">
          <span style={{ color: currentColors.text }}>
            {selectedProducts.length} product(s) selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => handleBulkAction('activate')}
              className="px-3 py-1.5 rounded-lg text-sm bg-green-500/20 text-green-400"
            >
              Activate
            </button>
            <button
              onClick={() => handleBulkAction('deactivate')}
              className="px-3 py-1.5 rounded-lg text-sm bg-yellow-500/20 text-yellow-400"
            >
              Deactivate
            </button>
            <button
              onClick={() => handleBulkAction('delete')}
              className="px-3 py-1.5 rounded-lg text-sm bg-red-500/20 text-red-400"
            >
              Delete
            </button>
          </div>
        </div>
      )}

      {/* Products Table */}
      <div className="glass-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 rounded-full animate-spin" style={{ borderColor: `${currentColors.primary}30`, borderTopColor: currentColors.primary }}></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ borderBottom: `1px solid ${currentColors.border}` }}>
                    <th className="p-4 text-left">
                      <input
                        type="checkbox"
                        checked={selectedProducts.length === products.length && products.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded"
                      />
                    </th>
                    <th className="p-4 text-left text-sm font-cinzel" style={{ color: currentColors.primary }}>Product</th>
                    <th className="p-4 text-left text-sm font-cinzel" style={{ color: currentColors.primary }}>SKU</th>
                    <th className="p-4 text-left text-sm font-cinzel" style={{ color: currentColors.primary }}>Category</th>
                    <th className="p-4 text-right text-sm font-cinzel" style={{ color: currentColors.primary }}>Price</th>
                    <th className="p-4 text-right text-sm font-cinzel" style={{ color: currentColors.primary }}>Cost</th>
                    <th className="p-4 text-right text-sm font-cinzel" style={{ color: currentColors.primary }}>Profit</th>
                    <th className="p-4 text-center text-sm font-cinzel" style={{ color: currentColors.primary }}>Stock</th>
                    <th className="p-4 text-center text-sm font-cinzel" style={{ color: currentColors.primary }}>Status</th>
                    <th className="p-4 text-right text-sm font-cinzel" style={{ color: currentColors.primary }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr 
                      key={product.id} 
                      className="hover:bg-white/5 transition-all"
                      style={{ borderBottom: `1px solid ${currentColors.border}20` }}
                    >
                      <td className="p-4">
                        <input
                          type="checkbox"
                          checked={selectedProducts.includes(product.id)}
                          onChange={() => toggleSelect(product.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {product.image_url ? (
                            <img src={product.image_url} alt={product.name} className="w-10 h-10 rounded-lg object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${currentColors.primary}20` }}>
                              <Image className="w-5 h-5" style={{ color: currentColors.primary }} />
                            </div>
                          )}
                          <div>
                            <p className="font-medium" style={{ color: currentColors.text }}>{product.name}</p>
                            {product.is_featured && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400">Featured</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-sm" style={{ color: currentColors.textMuted }}>{product.sku}</td>
                      <td className="p-4 text-sm" style={{ color: currentColors.textMuted }}>{product.category}</td>
                      <td className="p-4 text-right">
                        <div>
                          {product.discount_percent > 0 && (
                            <span className="text-xs line-through mr-2" style={{ color: currentColors.textMuted }}>
                              ${product.price.toFixed(2)}
                            </span>
                          )}
                          <span className="font-medium" style={{ color: currentColors.primary }}>
                            ${product.sale_price?.toFixed(2)}
                          </span>
                          {product.discount_percent > 0 && (
                            <span className="text-xs ml-1 text-green-400">-{product.discount_percent}%</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-right text-sm" style={{ color: currentColors.textMuted }}>
                        ${product.cost_price?.toFixed(2)}
                      </td>
                      <td className="p-4 text-right">
                        <span className={`text-sm ${product.calculated_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          ${product.calculated_profit?.toFixed(2)}
                        </span>
                        <span className="text-xs ml-1" style={{ color: currentColors.textMuted }}>
                          ({product.calculated_margin?.toFixed(1)}%)
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          product.is_low_stock 
                            ? 'bg-red-500/20 text-red-400' 
                            : 'bg-green-500/20 text-green-400'
                        }`}>
                          {product.stock_quantity}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          product.is_active 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {product.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => editProduct(product)}
                            className="p-2 rounded-lg transition-all hover:bg-white/10"
                            style={{ color: currentColors.primary }}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(product.id)}
                            className="p-2 rounded-lg transition-all hover:bg-red-500/10 text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between p-4" style={{ borderTop: `1px solid ${currentColors.border}` }}>
              <p className="text-sm" style={{ color: currentColors.textMuted }}>
                Showing {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination(p => ({ ...p, skip: Math.max(0, p.skip - p.limit) }))}
                  disabled={pagination.skip === 0}
                  className="p-2 rounded-lg disabled:opacity-50 transition-all"
                  style={{ backgroundColor: `${currentColors.primary}20`, color: currentColors.primary }}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setPagination(p => ({ ...p, skip: p.skip + p.limit }))}
                  disabled={pagination.skip + pagination.limit >= pagination.total}
                  className="p-2 rounded-lg disabled:opacity-50 transition-all"
                  style={{ backgroundColor: `${currentColors.primary}20`, color: currentColors.primary }}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Product Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="glass-card p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-cinzel" style={{ color: currentColors.primary }}>
                {editingProduct ? 'Edit Product' : 'Add Product'}
              </h2>
              <button onClick={resetForm} style={{ color: currentColors.textMuted }}>
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>SKU *</label>
                  <input
                    type="text"
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    className="cosmic-input w-full"
                    required
                    placeholder="PRD-001"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="cosmic-input w-full"
                    required
                    placeholder="Product Name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="cosmic-input w-full h-20 resize-none"
                  placeholder="Product description..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="cosmic-input w-full"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Selling Price *</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: currentColors.textMuted }} />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className="cosmic-input w-full pl-9"
                      required
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Cost Price *</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: currentColors.textMuted }} />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.cost_price}
                      onChange={(e) => setFormData({ ...formData, cost_price: e.target.value })}
                      className="cosmic-input w-full pl-9"
                      required
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Discount %</label>
                  <div className="relative">
                    <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: currentColors.textMuted }} />
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.discount_percent}
                      onChange={(e) => setFormData({ ...formData, discount_percent: e.target.value })}
                      className="cosmic-input w-full pl-9"
                      placeholder="0"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Fixed Margin %</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.profit_margin}
                    onChange={(e) => setFormData({ ...formData, profit_margin: e.target.value })}
                    className="cosmic-input w-full"
                    placeholder="Auto-calculated if empty"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Weight (kg)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.weight}
                    onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                    className="cosmic-input w-full"
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Profit Preview */}
              <div className="p-4 rounded-lg" style={{ backgroundColor: `${currentColors.primary}10` }}>
                <p className="text-sm font-medium mb-2" style={{ color: currentColors.primary }}>Profit Preview</p>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs" style={{ color: currentColors.textMuted }}>Sale Price</p>
                    <p className="font-medium" style={{ color: currentColors.text }}>${profitPreview.salePrice}</p>
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: currentColors.textMuted }}>Profit</p>
                    <p className={`font-medium ${parseFloat(profitPreview.profit) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      ${profitPreview.profit}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: currentColors.textMuted }}>Margin</p>
                    <p className="font-medium" style={{ color: currentColors.text }}>{profitPreview.margin}%</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Stock Quantity</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.stock_quantity}
                    onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                    className="cosmic-input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Low Stock Alert</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.low_stock_threshold}
                    onChange={(e) => setFormData({ ...formData, low_stock_threshold: e.target.value })}
                    className="cosmic-input w-full"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Product Image</label>
                <div className="space-y-2">
                  {/* Image Preview */}
                  {formData.image_url && (
                    <div className="relative w-32 h-32 rounded-lg overflow-hidden" style={{ backgroundColor: `${currentColors.primary}10` }}>
                      <img 
                        src={formData.image_url.startsWith('/') ? `${API.replace('/api', '')}${formData.image_url}` : formData.image_url} 
                        alt="Product" 
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, image_url: '' })}
                        className="absolute top-1 right-1 p-1 rounded-full bg-red-500 text-white hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  <div className="flex gap-2">
                    <input
                      type="file"
                      ref={imageInputRef}
                      onChange={handleImageUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => imageInputRef.current?.click()}
                      disabled={uploadingImage}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
                      style={{ backgroundColor: `${currentColors.primary}20`, color: currentColors.primary }}
                    >
                      {uploadingImage ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      {uploadingImage ? 'Uploading...' : 'Upload Image'}
                    </button>
                    <span className="text-xs self-center" style={{ color: currentColors.textMuted }}>or</span>
                    <input
                      type="url"
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      className="cosmic-input flex-1"
                      placeholder="Paste image URL..."
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Tags (comma separated)</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  className="cosmic-input w-full"
                  placeholder="tag1, tag2, tag3"
                />
              </div>

              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="rounded"
                  />
                  <span style={{ color: currentColors.text }}>Active</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                    className="rounded"
                  />
                  <span style={{ color: currentColors.text }}>Featured</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 rounded-lg"
                  style={{ backgroundColor: `${currentColors.textMuted}20`, color: currentColors.textMuted }}
                >
                  Cancel
                </button>
                <button type="submit" className="cosmic-btn">
                  {editingProduct ? 'Update Product' : 'Add Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
